import { useId, useRef } from 'react';

import { SHARED_COMPONENTS_TEXT } from '../constants';
import { BaseButton } from './BaseButton';

export interface BaseFilePickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  /** e.g. ".pdf,.xlsx" */
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  label?: string;
  /** Names the control for screen readers when several sit on one page (defaults to `label`). */
  'aria-label'?: string;
  /** Just the button: no "No file chosen" / file name text beside it. */
  compact?: boolean;
}

/** "Attach files": a real button plus inert text naming what was chosen (the native
 *  control fuses both into one clickable blob). */
export function BaseFilePicker({
  files,
  onChange,
  accept,
  multiple = true,
  disabled,
  label = SHARED_COMPONENTS_TEXT.filePicker.attachFiles,
  'aria-label': ariaLabel,
  compact = false,
}: BaseFilePickerProps) {
  const input = useRef<HTMLInputElement>(null);
  const id = useId();
  const summary =
    files.length === 0
      ? SHARED_COMPONENTS_TEXT.filePicker.noFileChosen
      : files.length === 1
        ? (files[0]?.name ?? '')
        : SHARED_COMPONENTS_TEXT.filePicker.filesChosen(files.length);
  return (
    <div className="flex min-w-0 items-center gap-2">
      <input
        ref={input}
        id={id}
        type="file"
        className="sr-only"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        onChange={(e) => {
          onChange(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
      <BaseButton
        size="sm"
        variant="secondary"
        icon="pi pi-paperclip"
        disabled={disabled}
        onClick={() => input.current?.click()}
      >
        {label}
      </BaseButton>
      {!compact && (
        <span className="truncate text-[12px] text-text-3" aria-live="polite">
          {summary}
        </span>
      )}
      {files.length > 0 && (
        <BaseButton
          size="sm"
          variant="ghost"
          icon="pi pi-times"
          aria-label={SHARED_COMPONENTS_TEXT.filePicker.removeChosen}
          onClick={() => {
            onChange([]);
          }}
        />
      )}
    </div>
  );
}
